import { isBrowser } from 'browser-or-node';
import { ApolloProvider } from '@apollo/react-hooks';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import React, { Fragment, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import useCreateStores from 'state';
import { StyleSheetManager } from 'styled-components';
import { Normalize } from 'styled-normalize';
import { GlobalStyles, EmbeddedStyles } from 'theme';
import { ThemeProvider } from 'theme/provider';
import { ZoomableImagePortalStyles } from 'components/zoomable-image';

import { StoresProvider } from 'contexts/stores';
import { ViewportProvider } from 'contexts/viewport';
import GlobalControllers from 'components/global-controllers';
import EmbeddedEventsController from 'components/embedded-events-controller';

import { SeoMenu } from 'components/seo-menu';
import { IdleDetection } from 'src/components/idle-detection';
import Ernie from 'components/ernie';
import Fallback from 'components/fallbacks';
import BackToTop from 'components/back-to-top';
import { ErrorFallback } from 'src/errors';
import { useInitGlobalGA, useTrackRouteChanges } from 'hooks/use-google-analytics';
import { useInitLogRocket } from 'hooks/use-init-log-rocket';
import { useInitDataDogLogs } from 'src/hooks/use-init-datadog-logs';
import { Analytics } from 'utils/analytics';
import { useApollo } from 'utils/apollo/client';

import QueryParamProvider from 'utils/query-param-provider';
import useFixLogRocket from 'shared/hooks/use-fix-log-rocket';
import PublicEnv from 'shared/utils/public-env';
import { initializeModuleFederation } from 'src/utils/module-federation/runtime-init';
import { defaultUser } from 'shared/constants/users';
import { LoadPerformanceMode } from 'src/components/load-performance-mode';
import { IncompleteAccountDetection } from 'src/components/incomplete-account-detection';
import { LoyaltyAccountDetection } from 'src/components/loyalty-account-detection';
import { AdsInspectorDevTool } from 'src/adtech/inspector/ads-inspector-devtool';

import { queryStore, QueryStoreProvider } from 'hooks/use-query';

import 'react-multi-carousel/lib/styles.css';

// NOTE: GlobalModals is deprecated. we are in a process of refactoring to Modals
const GlobalModals = dynamic(() => import('src/components/modals/global-modals'), { ssr: false });
const Modals = dynamic(() => import('src/components/modals/modals').then((mod) => mod.Modals), { ssr: false });
const FidesController = dynamic(() => import('src/utils/fides').then((mod) => mod.FidesController), { ssr: false });

// Want this called immediately so that remotes are available for the first page load
initializeModuleFederation();

// This will wrap every single page component
// this will *also* wrap every single storybook component!
function App({ Component, pageProps = {}, deviceType = `desktop` }) {
  // first thing's first....
  useFixLogRocket();
  const stores = useCreateStores();
  const apolloClient = useApollo(pageProps.initialApolloState);
  const router = useRouter();
  const isEmbedded = useObserver(() => stores.UI.isEmbedded);
  const Layout = Component.layout || Fragment;

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector(`#jss-server-side`);
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  // Enable Tracking & Logging Services
  useInitLogRocket(stores.UI);
  useInitGlobalGA(stores.FeatureFlags);
  useInitDataDogLogs();
  useTrackRouteChanges();

  return (
    <QueryParamProvider>
      <StoresProvider value={stores}>
        <StyleSheetManager>
          <ViewportProvider>
            <Normalize />
            <GlobalStyles />
            <ZoomableImagePortalStyles />
            {isEmbedded && <EmbeddedStyles />}
            {router.isFallback && <Fallback />}
            {!router.isFallback && (
              <ApolloProvider client={apolloClient}>
                <QueryStoreProvider value={queryStore}>
                  <GlobalControllers />
                  <FidesController />
                  <ThemeProvider deviceType={deviceType}>
                    <IdleDetection>
                      <EmbeddedEventsController />
                      <AdsInspectorDevTool />
                      <Analytics />
                      <GlobalModals />
                      <Modals />
                      <Ernie />
                      {isEmbedded && <BackToTop />}
                      <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[router.asPath]}>
                        <SeoMenu>
                          <IncompleteAccountDetection>
                            <LoyaltyAccountDetection>
                              <Layout {...pageProps}>
                                <LoadPerformanceMode />
                                <Component {...pageProps} />
                              </Layout>
                            </LoyaltyAccountDetection>
                          </IncompleteAccountDetection>
                        </SeoMenu>
                      </ErrorBoundary>
                    </IdleDetection>
                  </ThemeProvider>
                </QueryStoreProvider>
              </ApolloProvider>
            )}
          </ViewportProvider>
        </StyleSheetManager>
      </StoresProvider>
    </QueryParamProvider>
  );
}

const context = {
  kind: 'multi',
  user: defaultUser,
};

if (isBrowser) {
  if (window.reactEnv?.dispensaryId) {
    context['ecomm-dispensary'] = {
      key: window.reactEnv.dispensaryId,
    };
  }
  if (window.reactEnv?.enterpriseId) {
    context['ecomm-enterprise'] = {
      key: window.reactEnv.enterpriseId,
    };
  }
}

export default withLDProvider({
  clientSideID: PublicEnv.launchDarklyClientId,
  options: {
    evaluationReasons: true,
    flushInterval: 30000,
    bootstrap: isBrowser && window.__LD_Bootstrap ? window.__LD_Bootstrap : 'localStorage',
    // Configure relay proxy endpoints (SDK v3.x uses baseUrl, streamUrl, eventsUrl)
    ...(PublicEnv.launchDarklyBaseUri && { baseUrl: PublicEnv.launchDarklyBaseUri }),
    ...(PublicEnv.launchDarklyStreamUri && { streamUrl: PublicEnv.launchDarklyStreamUri }),
    ...(PublicEnv.launchDarklyEventsUri && { eventsUrl: PublicEnv.launchDarklyEventsUri }),
  },
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  context,
})(App);
